// frontend/src/hooks/useAudioRecorder.js
import { useRef, useState, useCallback, useEffect } from 'react';

// Convierte un Blob a base64 puro (sin el prefijo data:...;base64,)
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result || '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function useAudioRecorder() {
  const mediaRef = useRef(null);
  const streamRef = useRef(null); // Fix 3: track the active stream for unmount cleanup
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);

  const start = useCallback(async () => {
    setError(null);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream; // Fix 3: store stream reference

      // Bitrate bajo: voz mono ~24kbps => 10 min ≈ 1.8 MB (cabe inline en Gemini).
      // Permite notas largas (hasta ~10 min) sin pasar el cap de request.
      // Fix 1: if both MediaRecorder constructors throw, stop the stream before re-throwing.
      let mr;
      try {
        try {
          mr = new MediaRecorder(stream, { audioBitsPerSecond: 24000 });
        } catch (_e) {
          mr = new MediaRecorder(stream); // fallback si el navegador no acepta la opción
        }
      } catch (constructErr) {
        // Both constructors failed — release the mic immediately.
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setError('Não foi possível acessar o microfone');
        throw constructErr;
      }

      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      // Catches getUserMedia errors and any re-thrown constructor error.
      // Stream cleanup for getUserMedia failure (stream is still undefined here):
      if (stream && streamRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setError('Não foi possível acessar o microfone');
      throw e;
    }
  }, []);

  // Para la grabación y resuelve { base64, mimeType }
  const stop = useCallback(() => {
    return new Promise((resolve, reject) => {
      const mr = mediaRef.current;
      if (!mr) { resolve(null); return; }
      mediaRef.current = null; // Fix 2: evita doble-stop: una segunda llamada resuelve null
      mr.onstop = async () => {
        try {
          const mimeType = mr.mimeType || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type: mimeType });
          mr.stream.getTracks().forEach((t) => t.stop());
          streamRef.current = null; // Fix 3: stream stopped, clear the ref
          const base64 = await blobToBase64(blob);
          setRecording(false);
          resolve({ base64, mimeType });
        } catch (e) { reject(e); }
      };
      mr.stop();
    });
  }, []);

  // Fix 3: cleanup on unmount — release mic if user navigates away mid-recording
  useEffect(() => {
    return () => {
      // Stop the MediaRecorder (suppresses the onstop callback side-effects gracefully)
      const mr = mediaRef.current;
      if (mr) {
        mediaRef.current = null;
        try { mr.stop(); } catch (_) { /* ignore */ }
      }
      // Stop any stream tracks that are still live
      const stream = streamRef.current;
      if (stream) {
        streamRef.current = null;
        stream.getTracks().forEach((t) => {
          if (t.readyState === 'live') t.stop();
        });
      }
    };
  }, []);

  return { recording, error, start, stop };
}
